'use client';

import { useState, useEffect } from 'react';
import { LuUser, LuSettings, LuLogOut, LuUsers, LuLogIn, LuMonitor, LuRotateCcw, LuLayoutDashboard } from 'react-icons/lu';
import { PiTextAa } from 'react-icons/pi';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { useAuthControls } from '@core/ui/auth/useAuthControls';
import { AppAvatar } from '@core/ui/components/AppAvatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@core/ui/primitives/dropdown-menu';
import { WIDE_BREAKPOINT } from '@core/helpers/styles/layoutBreakpoints';

// Settings constants
const DEFAULT_FONT_SCALE = 100;
const PREFERRED_LAYOUT_WIDTH = 1440;

interface UserAvatarProps {
  fontScale?: number;
  layoutWidth?: number;
  onFontScaleChange?: (scale: number) => void;
  onLayoutWidthChange?: (width: number) => void;
}

/**
 * UserAvatar - User profile button with dropdown menu
 *
 * Shows the user's avatar with a dropdown for profile/sign-out and display settings.
 * The Layout Width slider is only visible on Wide screens (≥1440px).
 * For unauthenticated users, shows settings controls plus a sign-in option.
 */
export function UserAvatar({
  fontScale = 100,
  layoutWidth,
  onFontScaleChange,
  onLayoutWidthChange,
}: UserAvatarProps) {
  const { status, user, signIn, signOut, isSigningOut } = useAuthControls();
  const [isWideScreen, setIsWideScreen] = useState(false);
  const [maxLayoutWidth, setMaxLayoutWidth] = useState(2400);
  const minLayoutWidth = Math.min(PREFERRED_LAYOUT_WIDTH, maxLayoutWidth);
  const defaultLayoutWidth = minLayoutWidth;

  // Track if we're on a Wide screen (≥1440px) and update max layout width
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkWidth = () => {
      setIsWideScreen(window.innerWidth >= WIDE_BREAKPOINT);
      setMaxLayoutWidth(window.innerWidth);
    };

    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  const openProfileSettings = () => {
    window.dispatchEvent(new CustomEvent('profile.settings.open'));
  };

  const openOrganizationSettings = () => {
    window.dispatchEvent(new CustomEvent('organization.settings.open'));
  };

  // Show loading state
  if (status === 'loading') {
    return <div className='user-avatar-loading w-9 h-9 rounded-full bg-muted animate-pulse' />;
  }

  const isDefaultFontScale = fontScale === DEFAULT_FONT_SCALE;
  const isDefaultWidth = layoutWidth === defaultLayoutWidth;

  return (
    <>
      <style jsx global>{`
        [data-radix-popper-content-wrapper]:has(.user-avatar-dropdown) {
          top: -33px !important;
          left: 13px !important;
        }
      `}</style>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {user ? (
            <button
              className={cn(
                'user-avatar-button flex items-center justify-center transition-colors hover:opacity-90 focus-visible:outline-none'
              )}
              aria-label={user.name ? `Open profile menu for ${user.name}` : 'Open profile menu'}
              title={user.name || 'Profile'}>
              <AppAvatar
                userId={user.id}
                email={user.email}
                name={user.name}
                size='9'
              />
            </button>
          ) : (
            <button
              className={cn(
                'user-avatar-button w-9 h-9 rounded-full border-2 border-dashed flex items-center justify-center transition-colors',
                'border-border hover:border-muted-foreground text-muted-foreground hover:text-foreground focus-visible:outline-none'
              )}
              aria-label='Open profile menu'
              title='Profile'>
              <LuUser className='h-4 w-4' />
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' sideOffset={-18} className='user-avatar-dropdown w-64 relative rounded-t-none'>
          {/* User info section (only for signed-in users) */}
          {user && (
            <>
              <div className='user-avatar-info px-2 pr-12 relative h-14 flex flex-col justify-center'>
                {/* Avatar in top-right corner */}
                <div className='absolute top-1/2 -translate-y-1/2 right-2'>
                  <AppAvatar
                    userId={user.id}
                    email={user.email}
                    name={user.name}
                    size='9'
                  />
                </div>
                <p className='text-sm font-medium'>{user.name}</p>
                {user.email && <p className='text-xs text-muted-foreground'>{user.email}</p>}
              </div>
              <DropdownMenuSeparator />
            </>
          )}

          {/* Display Settings Section */}
          <DropdownMenuLabel className='user-avatar-settings-label text-xs font-medium uppercase tracking-wider text-muted-foreground'>
            Display Settings
          </DropdownMenuLabel>

          {/* Text Size Slider */}
          <div className='user-avatar-text-size px-2 py-2'>
            <div className='flex items-center justify-between mb-2'>
              <div className='flex items-center gap-2'>
                <PiTextAa className='h-4 w-4 text-muted-foreground' />
                <span className='text-sm'>Text Size</span>
              </div>
              <div className='flex items-center gap-1.5'>
                {!isDefaultFontScale && (
                  <button
                    onClick={() => onFontScaleChange?.(DEFAULT_FONT_SCALE)}
                    className='text-muted-foreground hover:text-foreground transition-colors'
                    aria-label='Reset text size'
                    title='Reset to default'>
                    <LuRotateCcw className='h-3 w-3' />
                  </button>
                )}
                <span className='text-xs font-mono text-muted-foreground'>{fontScale}%</span>
              </div>
            </div>
            <input
              type='range'
              min='80'
              max='150'
              step='1'
              value={fontScale}
              onChange={(e) => onFontScaleChange?.(Number(e.target.value))}
              className={cn(
                'w-full h-1.5 rounded-full appearance-none cursor-pointer',
                'bg-border',
                '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4',
                '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-foreground [&::-webkit-slider-thumb]:cursor-pointer',
                '[&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-brand-foreground/30'
              )}
            />
          </div>

          {/* Layout Width Slider - Only visible on Wide screens (≥1440px) */}
          {isWideScreen && (
            <div className='user-avatar-layout-width px-2 py-2'>
              <div className='flex items-center justify-between mb-2'>
                <div className='flex items-center gap-2'>
                  <LuMonitor className='h-4 w-4 text-muted-foreground' />
                  <span className='text-sm'>Layout Width</span>
                </div>
                <div className='flex items-center gap-1.5'>
                  {!isDefaultWidth && (
                    <button
                      onClick={() => onLayoutWidthChange?.(defaultLayoutWidth)}
                      className='text-muted-foreground hover:text-foreground transition-colors'
                      aria-label='Reset layout width'
                      title='Reset to default'>
                      <LuRotateCcw className='h-3 w-3' />
                    </button>
                  )}
                  <span className='text-xs font-mono text-muted-foreground'>{layoutWidth}px</span>
                </div>
              </div>
              <input
                type='range'
                min={minLayoutWidth}
                max={maxLayoutWidth}
                step='10'
                value={layoutWidth}
                onChange={(e) => onLayoutWidthChange?.(Number(e.target.value))}
                className={cn(
                  'w-full h-1.5 rounded-full appearance-none cursor-pointer',
                  'bg-border',
                  '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4',
                  '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-foreground [&::-webkit-slider-thumb]:cursor-pointer',
                  '[&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-brand-foreground/30'
                )}
              />
              <div className='flex justify-between mt-1 text-[10px] text-muted-foreground'>
                <span>{minLayoutWidth}px</span>
                <span>{maxLayoutWidth}px</span>
              </div>
            </div>
          )}

          <DropdownMenuSeparator />

          {/* Account actions */}
          {user ? (
            <>
              <DropdownMenuItem onClick={openProfileSettings}>
                <LuSettings className='mr-2 h-4 w-4' />
                Profile &amp; settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openOrganizationSettings}>
                <LuUsers className='mr-2 h-4 w-4' />
                Organizations
              </DropdownMenuItem>
              {user.isSuperAdmin && (
                <DropdownMenuItem onClick={() => window.dispatchEvent(new CustomEvent('platform.console.open'))}>
                  <LuLayoutDashboard className='mr-2 h-4 w-4' />
                  Platform console
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={signOut} disabled={isSigningOut}>
                <LuLogOut className='mr-2 h-4 w-4' />
                {isSigningOut ? 'Signing out...' : 'Sign out'}
              </DropdownMenuItem>
            </>
          ) : (
            <DropdownMenuItem onClick={signIn}>
              <LuLogIn className='mr-2 h-4 w-4' />
              Sign in
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

export default UserAvatar;
